import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getContractCustomers } from '../services/customer'
import { onPreview } from '../components/Detail/Attachment/columns'
import type { CustomerContract } from '../types'
import { ContractStatus } from '../types'
import type { DataTableAction } from '@/components/ui/data-table-customized'
import { buildAttachmentExpandable } from '@/components/ui/data-table-customized'
import EyeIcons from '@/icons/EyeIcons'
import DownLoadIcons from '@/icons/DownLoadIcons'
import ServiceReceiptIcons from '@/icons/ServiceReceiptIcons'
import {
    getContractColumns,
    getContractFilterItems,
} from '../utils/contract-list-config'

interface ContractFilters {
    contract_status: string[]
    contract_role: string[]
    contract_direction: string[]
    sla_enable: string[]
    nda_enable: string[]
    auto_renewable: string[]
    contract_effective_date: string[]
    contract_effective_date_to: string[]
    contract_expiry_date: string[]
    contract_expiry_date_to: string[]
    created_at: string[]
    created_at_to: string[]
}

export const useContractList = () => {
    const { t } = useTranslation()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ContractFilters>({
        defaults: { sort_by: 'contract_id', sort_order: 'desc', page_size: 10 },
        filterDefaults: {
            contract_status: [],
            contract_role: [],
            contract_direction: [],
            sla_enable: [],
            nda_enable: [],
            auto_renewable: [],
            contract_effective_date: [],
            contract_effective_date_to: [],
            contract_expiry_date: [],
            contract_expiry_date_to: [],
            created_at: [],
            created_at_to: [],
        },
    })

    const { control } = useForm<ContractFilters>({
        defaultValues: filters,
    })

    const { data, renderState, isFetching } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACTS, 'customers', queryParams],
            queryFn: () => getContractCustomers(queryParams),
            placeholderData: true,
        },
        { showLoading: false }
    )

    const handlePreview = useCallback(
        async (row: CustomerContract) => {
            const attachments = row?.attachments?.filter(
                (a) => !a?.attachment_closed
            )
            if (attachments?.length === 1) {
                onPreview(
                    row.contract_id,
                    `Xem hợp đồng - ${attachments[0]?.attachment_file_name}`
                )
                await openFilePreview(attachments[0])
            } else {
                toast.error(t('has-more-one-attachment'))
            }
        },
        [openFilePreview, t]
    )

    const handleDownload = useCallback(
        async (row: CustomerContract) => {
            const attachments = row?.attachments?.filter(
                (a) => !a?.attachment_closed
            )
            if (attachments?.length === 1) {
                onPreview(
                    row.contract_id,
                    `Tải hợp đồng - ${attachments[0]?.attachment_file_name}`
                )
                await downloadFileWithPresigned(attachments[0])
            } else {
                toast.error(t('has-more-one-attachment'))
            }
        },
        [downloadFileWithPresigned, t]
    )

    const columns = useMemo(() => getContractColumns(t), [t])
    const filterItems = useMemo(() => getContractFilterItems(t), [t])

    const actions: DataTableAction<CustomerContract>[] = [
        {
            key: 'preview',
            label: t('contract-preview'),
            icon: <EyeIcons className="w-4 h-4" />,
            onClick: handlePreview,
            disabled: (row) => row?.contract_status !== ContractStatus.Active,
        },
        {
            key: 'download',
            label: t('download-file'),
            icon: <DownLoadIcons className="w-4 h-4" />,
            onClick: handleDownload,
            disabled: (row) => row?.contract_status !== ContractStatus.Active,
        },
        {
            key: 'renew',
            label: t('contract-renew'),
            icon: <ServiceReceiptIcons className="w-4 h-4" />,
            disabled: (row) => row?.contract_status !== ContractStatus.Active,
            onClick: () => {},
        },
    ]

    const expandable = buildAttachmentExpandable<CustomerContract>({
        getAttachments: (row) => row.attachments || [],
        t,
        openFilePreview,
        downloadFileWithPresigned,
    })

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        contracts: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        params,
        filters,
        control,
        searchInputProps,
        columns,
        actions,
        expandable,
        filterItems,
        setFilter,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
